/**
 * Export and Transshipment Risk as a report: the agents' saved results read as
 * one verdict, key findings ranked by severity, the sources behind them, and
 * the network of checks, findings and sources the Network intelligence view
 * and the report PDF draw. Built from a RiskEvaluation, so the page and its
 * PDF say the same thing.
 */

import moment from 'moment';
import {
  RISK_AGENTS,
  RiskAgent,
  RiskEvaluation,
  RiskLevel,
} from './entityRiskEvaluation';

export interface RiskReportFinding {
  id: string;
  agent: RiskAgent;
  agentLabel: string;
  claim: string;
  severity: RiskLevel | null;
  category: string;
  sourceName: string;
  url: string;
  date: string;
  confidence: number | null;
}

export interface RiskReportSource {
  url: string;
  domain: string;
  /** The publisher the agents named, else the domain. */
  title: string;
  findingIds: string[];
  agents: RiskAgent[];
}

export interface RiskReportCheck {
  agent: RiskAgent;
  label: string;
  note: string;
  status: 'answered' | 'failed' | 'not-run';
  risk: RiskLevel | null;
  summary: string;
  riskDriver: string;
  confidence: number | null;
  findingIds: string[];
}

export interface EntityRiskReport {
  entityName: string;
  country: string;
  overallRisk: RiskLevel | null;
  /** One line on what drives the verdict, for the report header. */
  verdictNote: string;
  checkedAt: string | null;
  checks: RiskReportCheck[];
  findings: RiskReportFinding[];
  sources: RiskReportSource[];
  counts: {
    checks: number;
    answered: number;
    failed: number;
    findings: number;
    highFindings: number;
    sources: number;
  };
}

const SEVERITY_RANK: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

const severityRank = (severity: RiskLevel | null) =>
  severity ? SEVERITY_RANK[severity] : 0;

const asSeverity = (value: unknown): RiskLevel | null => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (text === 'high') return 'High';
  if (text === 'medium') return 'Medium';
  if (text === 'low') return 'Low';
  return null;
};

const clean = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

/**
 * The agent's source link, kept only when it is a web address: anything else
 * (javascript:, data:, malformed) is dropped, since the page links to it.
 */
const webUrl = (value: unknown): string => {
  const url = clean(value);
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : '';
  } catch {
    return '';
  }
};

/** example.com from https://www.example.com/story, or '' for a non-URL. */
export const sourceDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const humanCategory = (category: string): string =>
  category.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

/** Builds the report from an evaluation; checks with no answer stay listed. */
export const buildEntityRiskReport = (
  evaluation: RiskEvaluation,
  subject: { entityName: string; country?: string | null },
): EntityRiskReport => {
  const findings: RiskReportFinding[] = [];
  const checks: RiskReportCheck[] = RISK_AGENTS.map((meta) => {
    const result = evaluation.agents.find((item) => item.agent === meta.key);
    const findingIds: string[] = [];
    (result?.findings || []).forEach((finding, index) => {
      const claim = clean(finding?.claim);
      if (!claim) return;
      const id = `${meta.key}:${index}`;
      findingIds.push(id);
      findings.push({
        id: id,
        agent: meta.key,
        agentLabel: meta.label,
        claim: claim,
        severity: asSeverity(finding?.severity),
        category: humanCategory(clean(finding?.category)),
        sourceName: clean(finding?.source),
        url: webUrl(finding?.url),
        date: clean(finding?.date),
        confidence:
          typeof finding?.confidence === 'number' &&
          Number.isFinite(finding.confidence)
            ? finding.confidence
            : null,
      });
    });
    return {
      agent: meta.key,
      label: meta.label,
      note: meta.note,
      status: result?.status || 'not-run',
      risk: result?.risk || null,
      summary: clean(result?.summary),
      riskDriver: clean(result?.riskDriver),
      confidence: result?.confidence ?? null,
      findingIds: findingIds,
    };
  });

  // Most severe first; a check's own order breaks ties, so the list is stable.
  const agentOrder = RISK_AGENTS.map((meta) => meta.key);
  findings.sort(
    (a, b) =>
      severityRank(b.severity) - severityRank(a.severity) ||
      agentOrder.indexOf(a.agent) - agentOrder.indexOf(b.agent) ||
      a.id.localeCompare(b.id),
  );

  const byUrl = new Map<string, RiskReportSource>();
  findings.forEach((finding) => {
    if (!finding.url) return;
    const existing = byUrl.get(finding.url);
    if (existing) {
      existing.findingIds.push(finding.id);
      if (existing.agents.indexOf(finding.agent) === -1) {
        existing.agents.push(finding.agent);
      }
      return;
    }
    const domain = sourceDomain(finding.url);
    byUrl.set(finding.url, {
      url: finding.url,
      domain: domain,
      title: finding.sourceName || domain || finding.url,
      findingIds: [finding.id],
      agents: [finding.agent],
    });
  });
  const sources = Array.from(byUrl.values()).sort(
    (a, b) =>
      b.findingIds.length - a.findingIds.length ||
      a.title.localeCompare(b.title),
  );

  const answered = checks.filter((check) => check.status === 'answered');
  const drivers = answered
    .filter(
      (check) =>
        check.risk &&
        evaluation.overallRisk &&
        check.risk === evaluation.overallRisk,
    )
    .map((check) => check.label.toLowerCase());
  const verdictNote = !answered.length
    ? 'No check answered, so there is no verdict yet.'
    : !evaluation.overallRisk
    ? 'The checks that answered could not score this entity.'
    : `${evaluation.overallRisk} risk, driven by ${drivers.join(', ')}.`;

  return {
    entityName: subject.entityName,
    country: clean(subject.country),
    overallRisk: evaluation.overallRisk,
    verdictNote: verdictNote,
    checkedAt: evaluation.checkedAt,
    checks: checks,
    findings: findings,
    sources: sources,
    counts: {
      checks: checks.length,
      answered: evaluation.answered,
      failed: evaluation.failed,
      findings: findings.length,
      highFindings: findings.filter((finding) => finding.severity === 'High')
        .length,
      sources: sources.length,
    },
  };
};

/** Characters a file name cannot carry on the usual file systems. */
const UNSAFE_FILE_NAME = /[\\/:*?"<>|]+/g;

export const riskReportFileName = (report: EntityRiskReport): string => {
  const entity = String(report.entityName || 'Entity')
    .replace(UNSAFE_FILE_NAME, '-')
    .trim();
  const checked = moment(report.checkedAt || undefined);
  const date = (checked.isValid() ? checked : moment()).format('MM-DD-YYYY');
  return `Export and Transshipment Risk - ${entity} - ${date}.pdf`;
};
